import * as React from 'react';
import {cn} from 'src/lib/utils';

/**
 * A compact single-choice control, styled like the range toggle on account
 * balance charts.
 */
export const Segmented = <Key extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: Array<{key: Key; label: string}>;
  value: Key;
  onChange: (key: Key) => void;
}) => {
  const container = React.useRef<HTMLDivElement>(null);
  const active = React.useRef<HTMLButtonElement>(null);
  // Keep the chosen option in view when the control scrolls — sideways
  // only, so it never moves the page.
  React.useEffect(() => {
    const box = container.current;
    const button = active.current;
    if (!box || !button) return;
    if (button.offsetLeft < box.scrollLeft) {
      box.scrollLeft = button.offsetLeft;
    } else if (button.offsetLeft + button.offsetWidth > box.scrollLeft + box.clientWidth) {
      box.scrollLeft = button.offsetLeft + button.offsetWidth - box.clientWidth;
    }
  }, [value]);
  return (
    // Scrolls within itself rather than the page on a screen too narrow for
    // every option.
    <div
      ref={container}
      role="radiogroup"
      aria-label={label}
      className="border-border relative inline-flex max-w-full overflow-x-auto rounded-lg border [scrollbar-width:none]"
    >
      {options.map((option) => (
        <button
          key={option.key}
          ref={option.key === value ? active : undefined}
          type="button"
          role="radio"
          aria-checked={option.key === value}
          onClick={() => onChange(option.key)}
          className={cn(
            'shrink-0 px-2.5 py-1 text-xs transition',
            option.key === value
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:bg-muted',
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
};
